/** Задание фронта — это SubTask бэкенда; здесь только формы данных на проводе, без запросов. */

import { MmCompetitorStore, MmSkuKey, MmTask, MmTaskClassification } from '../types/domain';
import { MmTaskStatus, MmTaskType } from '../types/enums';
import {
  MmCreateContinuousTaskRequest,
  MmCreateFreeTaskRequest,
  MmCreateTaskResponse,
  MmCreateTpcTaskRequest,
  MmTaskFilters,
} from '../types/dto';

function readString(source: any, name: string): string | null {
  if (!source || typeof source !== 'object') {
    return null;
  }

  const value = source[name];

  return typeof value === 'string' && value !== '' ? value : null;
}

/** Поля нет — возвращаем null, а не false: «выключено» и «неизвестно» различаются */
function readBoolean(source: any, name: string): boolean | null {
  if (!source || typeof source !== 'object') {
    return null;
  }

  const value = source[name];

  return typeof value === 'boolean' ? value : null;
}

function readNumber(source: any, name: string): number | null {
  if (!source || typeof source !== 'object') {
    return null;
  }

  const value = source[name];

  return typeof value === 'number' && isFinite(value) ? value : null;
}

function readArray(source: any, name: string): any[] {
  if (!source || typeof source !== 'object') {
    return [];
  }

  const value = source[name];

  return Object.prototype.toString.call(value) === '[object Array]' ? value : [];
}

export function readTaskStatus(raw: any): MmTaskStatus | null {
  if (typeof raw !== 'string' || raw === '') {
    return null;
  }

  return raw.toLowerCase() as MmTaskStatus;
}

/** Верхний регистр обязателен: со строчным статусом сервер отвечает 400 */
export function writeTaskStatus(status: string): string {
  return String(status).toUpperCase();
}

/** Статусы шлём одной строкой через запятую: массив axios отправит как status[], бэкенд не примет */
export function buildTaskStatusQuery(statuses: string[] | null | undefined): string | null {
  if (!statuses || statuses.length === 0) {
    return null;
  }

  const codes: string[] = [];

  for (let i = 0; i < statuses.length; i += 1) {
    const code = statuses[i];

    if (typeof code === 'string' && code !== '') {
      codes.push(writeTaskStatus(code));
    }
  }

  return codes.length === 0 ? null : codes.join(',');
}

export function readTaskType(raw: any): MmTaskType {
  if (typeof raw === 'string' && raw !== '') {
    return raw.toUpperCase() as MmTaskType;
  }

  return MmTaskType.CONTINUOUS;
}

/** Карта «UUID снимка тега → числовой id каталога SmartPricing». */
interface MmTagIdIndex {
  [snapshotId: string]: number;
}

function buildTagIdIndex(taskRaw: any): MmTagIdIndex {
  const tags = readArray(taskRaw, 'selectedProductTags');
  const index: MmTagIdIndex = {};

  for (let i = 0; i < tags.length; i += 1) {
    const snapshotId = readString(tags[i], 'id');
    const externalId = readNumber(tags[i], 'externalProductTagId');

    if (snapshotId !== null && externalId !== null) {
      index[snapshotId] = externalId;
    }
  }

  return index;
}

/** Проверка typeof отсекает ключи прототипа; null означает «числового id нет», имя даёт сервер */
function readTagNumericId(index: MmTagIdIndex, snapshotId: string | null): number | null {
  if (snapshotId === null) {
    return null;
  }

  const value = index[snapshotId];

  return typeof value === 'number' ? value : null;
}

/** Пустой список тегов даёт null, а не «группу»: выдуманный уровень хуже отсутствующего */
function readMonitoringLevel(taskRaw: any): string | null {
  const tags = readArray(taskRaw, 'selectedProductTags');
  let hasSubcategory = false;
  let hasCategory = false;
  let hasGroup = false;

  for (let i = 0; i < tags.length; i += 1) {
    const type = readString(tags[i], 'type');
    const code = type === null ? '' : type.toUpperCase();

    if (code === 'SUBCATEGORY') {
      hasSubcategory = true;
    } else if (code === 'CATEGORY') {
      hasCategory = true;
    } else if (code === 'GROUP') {
      hasGroup = true;
    }
  }

  if (hasSubcategory) {
    return 'subcategory';
  }

  if (hasCategory) {
    return 'category';
  }

  return hasGroup ? 'group' : null;
}

/** Сеть и формат конкурента остаются null: в снимке их нет, competitorName — имя магазина */
function readCompetitorStore(subTaskRaw: any, tagIds: MmTagIdIndex): MmCompetitorStore | null {
  const snapshot = subTaskRaw && typeof subTaskRaw === 'object' ? subTaskRaw.storeSnapshot : null;

  if (!snapshot || typeof snapshot !== 'object') {
    return null;
  }

  return {
    competitorCode: readString(snapshot, 'competitorCode') || '',
    name: readString(snapshot, 'competitorName'),
    address: readString(snapshot, 'address'),
    competitorNetworkName: null,
    competitorFormat: null,
    priority: readNumber(snapshot, 'competitorPriority'),

    storeFormatId: readTagNumericId(tagIds, readString(snapshot, 'formatId')),
    storeFormatName: readString(snapshot, 'formatName'),
    storeRegionId: readTagNumericId(tagIds, readString(snapshot, 'regionId')),
    storeRegionName: readString(snapshot, 'regionName'),
    storeClusterId: readTagNumericId(tagIds, readString(snapshot, 'clusterId')),
    storeClusterName: readString(snapshot, 'clusterName'),
  };
}

/** Идентификаторы тегов на проводе — UUID, в домене числа: только через readTagNumericId */
function readClassifications(taskRaw: any, tagIds: MmTagIdIndex): MmTaskClassification[] {
  const rows = readArray(taskRaw, 'productSearchCriteria');
  const items: MmTaskClassification[] = [];

  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];

    items.push({
      id: readString(row, 'id') || '',
      taskId: readString(row, 'taskId') || '',
      groupId: readTagNumericId(tagIds, readString(row, 'groupId')),
      groupName: readString(row, 'groupName'),
      categoryId: readTagNumericId(tagIds, readString(row, 'categoryId')),
      categoryName: readString(row, 'categoryName'),
      subcategoryId: readTagNumericId(tagIds, readString(row, 'subcategoryId')),
      subcategoryName: readString(row, 'subcategoryName'),
    });
  }

  return items;
}

export function readTaskFromSubTask(taskRaw: any, subTaskRaw: any): MmTask {
  const auditorId = readString(subTaskRaw, 'assigneeId');
  const tagIds = buildTagIdIndex(taskRaw);
  const competitorStore = readCompetitorStore(subTaskRaw, tagIds);

  return {
    /* Задание на фронте — это подзадача бэкенда, а пачка — его задание */
    id: readString(subTaskRaw, 'id') || '',
    batchId: readString(taskRaw, 'id'),

    type: readTaskType(taskRaw ? taskRaw.type : null),
    /** Приведение намеренное: null здесь означал бы нарушение контракта, а подставлять ASSIGNED нельзя */
    status: readTaskStatus(subTaskRaw ? subTaskRaw.status : null) as MmTaskStatus,
    /** Бэкенд всегда пишет priceCheckerEnabled = false, признаком KVI это поле не является */
    isPriceCheckerEnabled: readBoolean(taskRaw, 'priceCheckerEnabled'),
    createdAt: readString(taskRaw, 'createdAt'),
    completedAt: readString(subTaskRaw, 'endedAt'),
    auditorId: auditorId,

    comment: readString(taskRaw, 'comment'),

    competitorCode: competitorStore !== null ? competitorStore.competitorCode : '',
    competitorStore: competitorStore,

    storeFormatId: competitorStore !== null ? competitorStore.storeFormatId : null,
    storeFormatName: competitorStore !== null ? competitorStore.storeFormatName : null,
    storeRegionId: competitorStore !== null ? competitorStore.storeRegionId : null,
    storeRegionName: competitorStore !== null ? competitorStore.storeRegionName : null,
    storeClusterId: competitorStore !== null ? competitorStore.storeClusterId : null,
    storeClusterName: competitorStore !== null ? competitorStore.storeClusterName : null,

    monitoringLevel: readMonitoringLevel(taskRaw),

    classifications: readClassifications(taskRaw, tagIds),

    displayNumber: null,

    auditorLogin: null,
    auditorFullName: null,

    /** positionsTotal — доехавшие строки (resultsCount), а positionsWithPrice бэкенд не присылает */
    positionsTotal: readNumber(subTaskRaw, 'resultsCount'),
    positionsWithPrice: null,
  };
}

/** Ответ приходит и голым массивом, и конвертом с items, поэтому readPage здесь неприменим */
export function readTasks(raw: any): MmTask[] {
  const rows = Object.prototype.toString.call(raw) === '[object Array]' ? raw : readArray(raw, 'items');
  const tasks: MmTask[] = [];

  for (let i = 0; i < rows.length; i += 1) {
    const taskRaw = rows[i];
    const subTasks = readArray(taskRaw, 'subTasks');

    for (let j = 0; j < subTasks.length; j += 1) {
      tasks.push(readTaskFromSubTask(taskRaw, subTasks[j]));
    }
  }

  return tasks;
}

export function readCreatedTasks(raw: any): MmCreateTaskResponse {
  const subTasks = readArray(raw, 'subTasks');
  const ids: string[] = [];

  for (let i = 0; i < subTasks.length; i += 1) {
    const id = readString(subTasks[i], 'id');

    if (id !== null) {
      ids.push(id);
    }
  }

  return {
    createdTaskIds: ids,
    createdCount: ids.length,
    batchId: readString(raw, 'id'),
  };
}

function readIds(values: number[] | null | undefined): number[] {
  if (!values || values.length === 0) {
    return [];
  }

  const ids: number[] = [];

  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];

    if (typeof value === 'number' && isFinite(value)) {
      ids.push(value);
    }
  }

  return ids;
}

/** tradingNetworkId в тело не кладём — сеть бэкенд берёт из токена; пустые массивы шлём явно. */
export function buildStoresFilter(filters: MmTaskFilters): { [key: string]: any } {
  return {
    formatsIds: readIds(filters ? filters.formats : null),
    regionsIds: readIds(filters ? filters.regions : null),
    clustersIds: readIds(filters ? filters.clusters : null),
  };
}

export function buildProductsSearchCriteria(filters: MmTaskFilters): { [key: string]: any } {
  return {
    groupsIds: readIds(filters ? filters.groups : null),
    categoriesIds: readIds(filters ? filters.categories : null),
    subcategoriesIds: readIds(filters ? filters.subCategories : null),
  };
}

function buildCreateSections(filters: MmTaskFilters): { [key: string]: any } {
  return {
    storesFilter: buildStoresFilter(filters),
    productsSearchCriteria: buildProductsSearchCriteria(filters),
  };
}

export function buildCreateContinuousTaskBody(payload: MmCreateContinuousTaskRequest): { [key: string]: any } {
  const filters = payload ? payload.filters : null;
  const comment = payload && typeof payload.comment === 'string' && payload.comment !== '' ? payload.comment : null;
  const body = buildCreateSections(filters as MmTaskFilters);

  body.comment = comment;

  return body;
}

/** Отдельный сборщик, хотя тело совпадает со Сплошным: правила проверки у типов уже разные */
export function buildCreateTpcTaskBody(payload: MmCreateTpcTaskRequest): { [key: string]: any } {
  const filters = payload ? payload.filters : null;
  const comment = payload && typeof payload.comment === 'string' && payload.comment !== '' ? payload.comment : null;
  const body = buildCreateSections(filters as MmTaskFilters);

  body.comment = comment;

  return body;
}

/** Свёртка ключей в коды теряет кластер: бэкенд принимает плоский список строк */
function readInternalCodes(keys: MmSkuKey[] | null | undefined): string[] {
  if (!keys || keys.length === 0) {
    return [];
  }

  const codes: string[] = [];
  const seen: { [code: string]: boolean } = {};

  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const code = key && typeof key.internalCode === 'string' ? key.internalCode : '';

    if (code !== '' && seen[code] !== true) {
      seen[code] = true;
      codes.push(code);
    }
  }

  return codes;
}

export function buildCreateFreeTaskBody(payload: MmCreateFreeTaskRequest): { [key: string]: any } {
  const filters = payload ? payload.filters : null;
  const comment = payload && typeof payload.comment === 'string' && payload.comment !== '' ? payload.comment : null;
  const selection = payload ? payload.selectCheckBoxesData : null;
  const isCommonCheckboxSelected =
    selection && typeof selection.isCommonCheckboxSelected === 'boolean' ? selection.isCommonCheckboxSelected : false;

  /** У свободного секции лежат внутри productsData; на верхнем уровне только productsData и comment */
  const productsData = buildCreateSections(filters as MmTaskFilters);

  productsData.selectCheckBoxesData = {
    isCommonCheckboxSelected: isCommonCheckboxSelected,
    includedSKUsInternalCodes: readInternalCodes(selection ? selection.includedSKUsInternalCodes : null),
    excludedSKUsInternalCodes: readInternalCodes(selection ? selection.excludedSKUsInternalCodes : null),
  };

  return {
    productsData: productsData,
    comment: comment,
    priceCheckerEnabled: payload && payload.isPriceCheckerEnabled === true,
  };
}

/** Приёмка синхронна: operationId приходит null, ждать за ним прогресса не нужно */
export function readAcceptOperationId(raw: any): string | null {
  return readString(raw, 'operationId');
}
